import { S } from './state.js';
import { cardLayout } from './layout.js';
import { Audio } from './audio.js';
import { BAL } from './config.js';
import { GEAR_TIERS } from './data/gear.js';
import { ARCH_INFO, ITEMS, TIERS } from './data/items.js';
import { GAME_VERSION } from './data/progression.js';
import { WEAPONS, WEAPON_IDX, gearInteractionNote } from './data/weapons.js';
import { persist } from './economy.js';
import { API, API_RUN } from './meter.js';
import { player } from './entities.js';
import { ctx } from './canvas.js';
import { aim, pointIn } from './input.js';
import { die } from './physics.js';
import { autoRing, buyGear, closeShop, rerollShop, shopRerollCost } from './shop.js';
import { drawStatCard } from './shrines.js';
import { roundRect } from './screens.js';
import { fmtTime, gunCadence, gunFloor, hexA, paperDps } from './render.js';
// ---- Wave shop ----
// draw the gear collection as wrapping tier-colored chips. The collection is UNBOUNDED, so callers
// pass maxRows — past that the rest collapses into one "+N" chip instead of overflowing the panel.
export function drawGearStrip(x, y, maxW, size, maxRows) {
  // group identical pieces into ONE chip with a ×count (uniques by key, generic rolls by icon+tier) —
  // player feedback: "my stuff shows one by one instead of ×3 / ×6"
  const groups = [], gidx = {};
  for (const g of S.equipment) {
    const k = g.unique || (g.icon + '|' + g.tier);
    if (gidx[k] == null) { gidx[k] = groups.length; groups.push({ g, n: 0 }); }
    groups[gidx[k]].n++;
  }
  const perRow = Math.max(1, Math.floor((maxW + 6) / (size + 6)));
  const cap = maxRows ? maxRows * perRow : Infinity;
  const showN = groups.length > cap ? cap - 1 : groups.length;   // save a slot for the "+N" chip
  let cx = x, cy = y;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let i = 0; i < showN; i++) {
    const gr = groups[i];
    if (cx + size > x + maxW) { cx = x; cy += size + 6; }
    ctx.fillStyle = 'rgba(255,255,255,.05)';
    roundRect(cx, cy, size, size, 7); ctx.fill();
    ctx.strokeStyle = hexA(GEAR_TIERS[gr.g.tier].color, 0.8); ctx.lineWidth = 1.5; ctx.stroke();
    ctx.font = Math.floor(size * 0.5) + 'px system-ui'; ctx.fillStyle = '#fff';
    ctx.fillText(gr.g.icon, cx + size / 2, cy + size / 2 + 1);
    if (gr.n > 1) {   // stack badge
      ctx.textAlign = 'right';
      ctx.font = 'bold ' + Math.max(9, Math.floor(size * 0.34)) + 'px system-ui'; ctx.fillStyle = '#ffd66e';
      ctx.fillText('×' + gr.n, cx + size - 2, cy + size - 7);
      ctx.textAlign = 'center';
    }
    cx += size + 6;
  }
  if (showN < groups.length) {
    if (cx + size > x + maxW) { cx = x; cy += size + 6; }
    ctx.fillStyle = 'rgba(255,255,255,.05)'; roundRect(cx, cy, size, size, 7); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.25)'; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.font = 'bold ' + Math.floor(size * 0.36) + 'px system-ui'; ctx.fillStyle = '#cdd6ff';
    ctx.fillText('+' + (groups.length - showN), cx + size / 2, cy + size / 2 + 1);
  }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  return cy + size;   // bottom y
}

export function drawShopCard(g, x, y, w, h, i) {
  const tcol = g ? GEAR_TIERS[g.tier].color : '#888';
  const special = g && (g.tier === 'legendary' || g.tier === 'lunar');
  ctx.fillStyle = 'rgba(255,255,255,.04)';
  roundRect(x, y, w, h, 12); ctx.fill();
  // pulsing glow border for rare gear so legendaries pop
  if (special) {
    ctx.save();
    ctx.shadowColor = tcol; ctx.shadowBlur = 12 + 7 * Math.sin(performance.now() * 0.005);
    ctx.strokeStyle = tcol; ctx.lineWidth = 2.5;
    roundRect(x, y, w, h, 12); ctx.stroke();
    ctx.restore();
  } else {
    ctx.strokeStyle = hexA(tcol, 0.6); ctx.lineWidth = 2; ctx.stroke();
  }
  if (!g) return;

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.font = '34px system-ui'; ctx.fillStyle = '#fff';
  ctx.fillText(g.icon, x + w / 2, y + 48);
  ctx.font = 'bold 16px system-ui'; ctx.fillStyle = tcol;
  ctx.fillText(g.name, x + w / 2, y + 74);
  ctx.font = '10px system-ui'; ctx.fillStyle = hexA(tcol, 0.9);
  ctx.fillText(g.tier.toUpperCase(), x + w / 2, y + 90);

  // stat lines
  ctx.textAlign = 'left'; ctx.font = '12px system-ui'; ctx.fillStyle = '#cdd6ff';
  let ly = y + 114;
  for (const line of g.lines) { ctx.fillText('• ' + line, x + 16, ly); ly += 18; }
  if (g.uniqueDesc) {
    ctx.fillStyle = '#ffd66e'; ctx.font = 'italic 12px system-ui';
    wrapText('★ ' + g.uniqueDesc, x + 16, ly + 2, w - 32, 16);
  }
  const gnote = gearInteractionNote(g.unique);   // this unique behaves differently on your current weapon
  if (gnote) {
    ctx.fillStyle = '#ffb74d'; ctx.font = 'italic 11px system-ui';
    ctx.fillText(fitTip(gnote, w - 32, x + 16, y + h - 72, 14), x + 16, y + h - 62);
  }

  // buy button / sold
  ctx.textAlign = 'center';
  const bw = w - 28, bh = 40, bx = x + 14, by = y + h - bh - 12;
  if (g.sold) {                                    // the one you chose
    ctx.fillStyle = 'rgba(95,220,122,.18)'; roundRect(bx, by, bw, bh, 9); ctx.fill();
    ctx.fillStyle = '#5fdc7a'; ctx.font = 'bold 15px system-ui'; ctx.textBaseline = 'middle';
    ctx.fillText('TAKEN', bx + bw / 2, by + bh / 2);
  } else if (S.shopPicked) {                         // already picked another — locked out
    ctx.fillStyle = 'rgba(255,255,255,.05)'; roundRect(bx, by, bw, bh, 9); ctx.fill();
    ctx.fillStyle = '#6b76a0'; ctx.font = 'bold 15px system-ui'; ctx.textBaseline = 'middle';
    ctx.fillText('—', bx + bw / 2, by + bh / 2);
  } else {
    const hot = pointIn(aim, bx, by, bw, bh);
    ctx.fillStyle = hot ? '#36c07a' : '#2a9c63';
    roundRect(bx, by, bw, bh, 9); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = 'bold 15px system-ui'; ctx.textBaseline = 'middle';
    ctx.fillText('TAKE', bx + bw / 2, by + bh / 2);
    S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => buyGear(i) });
  }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}

// truncate a single line with an ellipsis so it can never escape its panel (uses the CURRENT ctx.font)
export function fitText(s, maxW) {
  if (ctx.measureText(s).width <= maxW) return s;
  while (s.length > 1 && ctx.measureText(s + '…').width > maxW) s = s.slice(0, -1);
  return s + '…';
}
// fitText + hover tooltip: when the text actually got truncated, register a hover zone (bx/byTop/bh
// describe where the line is drawn) — drawTooltips() then shows the FULL text near the cursor.
S.tooltips = [];   // rebuilt every render frame, like uiButtons
export function fitTip(s, maxW, bx, byTop, bh) {
  const out = fitText(s, maxW);
  if (out !== s) S.tooltips.push({ x: bx, y: byTop, w: maxW, h: bh || 16, text: s });
  return out;
}
export function drawTooltips() {
  let t = null;
  for (const z of S.tooltips) if (aim.x >= z.x && aim.x <= z.x + z.w && aim.y >= z.y && aim.y <= z.y + z.h) { t = z; break; }
  if (!t) return;
  ctx.save();
  ctx.font = '12px system-ui';
  const maxW = 280, words = t.text.split(' '), lines = [];
  let line = '';
  for (const w2 of words) {
    if (ctx.measureText(line + w2 + ' ').width > maxW && line) { lines.push(line.trim()); line = w2 + ' '; }
    else line += w2 + ' ';
  }
  if (line.trim()) lines.push(line.trim());
  let bw = 0; for (const l of lines) bw = Math.max(bw, ctx.measureText(l).width);
  bw += 20;
  const bh2 = lines.length * 15 + 13;
  let bx2 = Math.max(8, Math.min(S.W - bw - 8, aim.x + 14));
  let by2 = aim.y - bh2 - 12;
  if (by2 < 8) by2 = aim.y + 20;
  ctx.fillStyle = 'rgba(8,12,26,.97)'; roundRect(bx2, by2, bw, bh2, 8); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.3)'; ctx.lineWidth = 1; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  lines.forEach((l, i) => ctx.fillText(l, bx2 + 10, by2 + 17 + i * 15));
  ctx.restore();
}
// returns the y of the LAST line drawn (callers can flow content below). Optional maxLines: the final
// line ellipsizes and the full text registers as a hover tooltip (0.14 truncation-tooltip pattern) —
// long mythic descs were silently running over the fixed-position labels beneath them.
export function wrapText(text, x, y, maxW, lh, maxLines) {
  const words = text.split(' '); let line = '', yy = y, n = 1;
  for (const w of words) {
    if (ctx.measureText(line + w + ' ').width > maxW && line) {
      if (maxLines && n >= maxLines) {                       // out of room — ellipsize, hover shows the rest
        line = line.trim();
        while (line && ctx.measureText(line + '…').width > maxW) line = line.slice(0, -1);
        ctx.fillText(line + '…', x, yy);
        S.tooltips.push({ x: ctx.textAlign === 'center' ? x - maxW / 2 : x, y: y - 10, w: maxW, h: yy - y + 16, text });
        return yy;
      }
      ctx.fillText(line, x, yy); line = w + ' '; yy += lh; n++;
    }
    else line += w + ' ';
  }
  ctx.fillText(line, x, yy);
  return yy;
}

export function shopMenu() {
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.85)'; ctx.fillRect(0, 0, S.W, S.H);
  const L = cardLayout(3, { maxW: 760, footer: 64 });
  const { px, py, pw, ph } = L, cardW = L.cardW, cardH = L.cardH;
  ctx.fillStyle = 'rgba(12,18,38,.95)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = 'bold 28px system-ui';
  ctx.fillText('🛒 SHOP', px + 28, py + 42);
  ctx.fillStyle = '#9fb0e0'; ctx.font = '13px system-ui';
  ctx.fillText(fitText(`Wave ${S.waveNum} cleared · take ONE free, or reroll for coins`, pw - 130), px + 28, py + 64);
  ctx.textAlign = 'right'; ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 22px system-ui';
  ctx.fillText('🪙 ' + S.coinsRun, px + pw - 28, py + 42);
  ctx.textAlign = 'left';

  // 3 rarity-rolled stat cards (same renderer as the level-up draft); TAKE applies it and advances
  for (let i = 0; i < 3; i++) drawStatCard(S.shopOffer[i], i, L.cardX(i), L.cardY(i), cardW, cardH, () => buyGear(i));

  // footer: Reroll (free spins / coins) | Skip, pinned to the bottom
  const btnH = 46, btnY = py + ph - btnH - 12, halfW = (pw - 56 - 16) / 2;
  const rw = Math.min(230, halfW), rx = px + 28;
  const canReroll = !S.shopPicked && (S.freeRerolls > 0 || S.coinsRun >= shopRerollCost());
  const rhot = pointIn(aim, rx, btnY, rw, btnH) && canReroll;
  ctx.fillStyle = canReroll ? (rhot ? '#3aa0ff' : '#2b6fd6') : 'rgba(120,130,160,.25)';
  roundRect(rx, btnY, rw, btnH, 12); ctx.fill();
  ctx.fillStyle = canReroll ? '#fff' : '#7a86ad'; ctx.font = 'bold 15px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(S.freeRerolls > 0 ? '↻ Reroll FREE ×' + S.freeRerolls : '↻ Reroll 🪙 ' + shopRerollCost(), rx + rw / 2, btnY + btnH / 2);
  if (canReroll) S.uiButtons.push({ x: rx, y: btnY, w: rw, h: btnH, action: rerollShop });

  const cw = Math.min(230, halfW), cx = px + pw - cw - 28;
  const chot = pointIn(aim, cx, btnY, cw, btnH);
  ctx.fillStyle = chot ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.08)'; roundRect(cx, btnY, cw, btnH, 12); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.4; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 16px system-ui';
  ctx.fillText('SKIP ▶', cx + cw / 2, btnY + btnH / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: cx, y: btnY, w: cw, h: btnH, action: closeShop });
}

// ---- Pause menu / equipment screen ----
export function drawToggle(x, y, w, h, label, on, action) {
  const hot = pointIn(aim, x, y, w, h);
  ctx.fillStyle = on ? (hot ? '#2a9c63' : '#247a52') : (hot ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.07)');
  roundRect(x, y, w, h, 9); ctx.fill();
  ctx.strokeStyle = on ? '#36c07a' : 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 13px system-ui'; ctx.fillText(fitText(label, w - 44), x + 12, y + h / 2);   // fit clear of the ON/OFF on the right
  ctx.textAlign = 'right'; ctx.fillStyle = on ? '#7dffb0' : '#8a97c4'; ctx.fillText(on ? 'ON' : 'OFF', x + w - 12, y + h / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x, y, w, h, action });
}
export function pauseOverlay() {
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.8)'; ctx.fillRect(0, 0, S.W, S.H);

  const pw = Math.min(780, S.W - 40), ph = Math.min(580, S.H - 40);
  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(12,18,38,.94)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = 'bold 32px system-ui';
  ctx.fillText('PAUSED', px + 28, py + 46);

  // live stat readout (weapon- and Fervor-aware, matching the in-run HUD)
  const wp = WEAPONS[S.weapon], effShots = (wp.shots || 1) + ((S.EVO && S.EVO.shots) || 0) + (S.MULTISHOT - 1) + S.PELLETS;
  const dps = paperDps().toFixed(0);
  ctx.fillStyle = '#9fb0e0'; ctx.font = '13px system-ui';
  // ⛔/knee markers (0.19): capped values are called out here too, not just in the stats panel
  const _capNow = S.arch.tank >= 3 ? BAL.player.armorCapTank : BAL.player.armorCap;
  const _rateMark = gunCadence(wp) <= gunFloor(wp) ? ' ⛔floor→dmg' : S.RATE_KNEED ? ' (knee)' : '';
  ctx.fillText(fitText(
    `HP ${Math.ceil(Math.max(0, player.hp))}/${S.MAX_HP}   ·   DMG ${S.DAMAGE.toFixed(0)}×${effShots}   ·   gun ~${dps} dps   ·   rate ${(60 / Math.max(gunFloor(wp), gunCadence(wp))).toFixed(1)}/s${_rateMark}   ·   ` +
    `crit ${(S.CRIT * 100).toFixed(0)}%×${S.CRITMULT.toFixed(1)}${S.CRIT >= 0.95 ? ' ⛔' : ''}   ·   ${S.PIERCE > 0 ? 'pierce ' + S.PIERCE + '   ·   ' : ''}` +
    `move ${Math.min(13, S.MAX_SPEED * 0.32 * S.fvSpeed).toFixed(1)}   ·   armor ${(S.ARMOR * 100).toFixed(0)}% (cap ${Math.round(_capNow * 100)}%)${S.ARMOR >= _capNow - 0.001 ? ' ⛔' : ''}`,
    pw - 56), px + 28, py + 70);
  ctx.textAlign = 'right'; ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 20px system-ui';
  ctx.fillText('🪙 ' + S.coinsRun + '   Wave ' + S.waveNum, px + pw - 28, py + 44);
  ctx.textAlign = 'left';

  // build identity + archetype investment (which themes you've stacked)
  ctx.fillStyle = S.buildTag ? '#9be8c0' : '#6b76a0'; ctx.font = 'bold 13px system-ui';
  let bstr = 'Build: ' + (S.buildTag ? ARCH_INFO[S.buildTag].icon + ' ' + S.buildName : 'Generalist (spread thin)');
  const inv = Object.keys(S.arch).filter(t => S.arch[t] > 0).sort((a, b) => S.arch[b] - S.arch[a]).map(t => ARCH_INFO[t].icon + S.arch[t]);
  if (inv.length) bstr += '    ' + inv.join('  ');
  ctx.fillText(fitText(bstr, pw - 56), px + 28, py + 88);

  // gear collection (unbounded — capped at 2 chip rows, the rest folds into a "+N" chip)
  ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 14px system-ui';
  ctx.fillText('Gear  (' + S.equipment.length + ' — from bosses & drops)', px + 28, py + 100);
  const gearBottom = drawGearStrip(px + 28, py + 108, pw - 56, 30, 2);

  // bottom band: assist toggles (3-across on wide panels, 2-across on narrow ones) + the action-button
  // row. Sizes are computed up front so the shrine-items list above knows where it must stop.
  const narrow = pw < 620;
  const tCols = narrow ? 2 : 3, tRows = Math.ceil(6 / tCols);
  const tGap = 10, th = 34, tw = (pw - 56 - tGap * (tCols - 1)) / tCols;
  const actY = py + ph - 56;                  // action-button row top
  const tgY = actY - 16 - tRows * (th + 8);   // toggles band top — the items list stops above this
  ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 14px system-ui';
  ctx.fillText('Items  (stacking — from shrines)', px + 28, gearBottom + 26);

  const owned = ITEMS.filter(it => S.items[it.key]);
  const itemsY = gearBottom + 40;
  if (!owned.length) {
    ctx.fillStyle = '#6b76a0'; ctx.font = '13px system-ui';
    ctx.fillText('No items yet — open 🛒 item caches (E) to buy them.', px + 28, itemsY + 14);
  } else {
    const cols = Math.max(1, Math.floor((pw - 40) / 248));
    const cw = (pw - 40) / cols;
    const rowsFit = Math.max(1, Math.floor((tgY - 12 - itemsY) / 56));
    const maxCards = cols * rowsFit;
    const showN = owned.length > maxCards ? maxCards - 1 : owned.length;   // save a slot for "+N more"
    owned.slice(0, showN).forEach((it, i) => {
      const cx = px + 20 + (i % cols) * cw;
      const cy = itemsY + Math.floor(i / cols) * 56;
      drawEquipCard(it, cx, cy, cw - 12, 48);
    });
    if (showN < owned.length) {
      const cx = px + 20 + (showN % cols) * cw;
      const cy = itemsY + Math.floor(showN / cols) * 56;
      ctx.fillStyle = 'rgba(255,255,255,.04)'; roundRect(cx, cy, cw - 12, 48, 10); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,.15)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#8a97c4'; ctx.font = 'bold 13px system-ui';
      ctx.fillText('+' + (owned.length - showN) + ' more — see Journal (J)', cx + (cw - 12) / 2, cy + 25);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    }
  }

  // toggles — key hints (F/M/T/B) only on desktop; drawToggle fits the label to the button width
  const hk = (lbl, k) => (!S.isTouch && k ? lbl + ' (' + k + ')' : lbl);
  const toggles = [
    [hk('Reduce flashing', 'F'), S.save.settings.reduceFlash, () => { S.save.settings.reduceFlash = !S.save.settings.reduceFlash; persist(); }],
    [hk('Screen shake', ''),     S.save.settings.shake,       () => { S.save.settings.shake = !S.save.settings.shake; persist(); }],
    [hk('Mute', 'M'),            Audio.isMuted(),             () => Audio.toggle()],
    [hk('Auto-aim', 'T'),        S.save.settings.autoAim,     () => { S.save.settings.autoAim = !S.save.settings.autoAim; persist(); }],
    [hk('Auto-shop', ''),        S.save.settings.autoShop,    () => { S.save.settings.autoShop = !S.save.settings.autoShop; persist(); }],
    [hk('Stats', 'B'),           S.save.settings.statsPanel,  () => { S.save.settings.statsPanel = !S.save.settings.statsPanel; persist(); }],
  ];
  toggles.forEach(([lbl, on, act], i) => {
    drawToggle(px + 28 + (i % tCols) * (tw + tGap), tgY + Math.floor(i / tCols) * (th + 8), tw, th, lbl, on, act);
  });

  // action buttons — JOURNAL · TALENTS · RESUME · GIVE UP in ONE fitted row (fitText keeps them on phone)
  const aGap = 8, aTot = pw - 48 - aGap * 3, abh = 46;
  const jw = Math.round(aTot * 0.23), lw = Math.round(aTot * 0.23), rw = Math.round(aTot * 0.30), gw = aTot - jw - lw - rw;
  ctx.textBaseline = 'middle';
  let abx = px + 24;
  const jhot = pointIn(aim, abx, actY, jw, abh);   // JOURNAL (left)
  ctx.fillStyle = jhot ? 'rgba(127,182,255,.55)' : 'rgba(127,182,255,.18)'; roundRect(abx, actY, jw, abh, 12); ctx.fill();
  ctx.fillStyle = jhot ? '#fff' : '#9fc6ff'; ctx.font = 'bold 14px system-ui'; ctx.textAlign = 'center';
  ctx.fillText(fitText('📖 Journal' + (S.isTouch ? '' : ' (J)'), jw - 12), abx + jw / 2, actY + abh / 2);
  S.uiButtons.push({ x: abx, y: actY, w: jw, h: abh, action: () => { S.showJournal = true; S.journalScroll = 0; } });
  abx += jw + aGap;
  const lhot = pointIn(aim, abx, actY, lw, abh);   // TALENTS
  ctx.fillStyle = lhot ? 'rgba(255,210,74,.5)' : 'rgba(255,210,74,.16)'; roundRect(abx, actY, lw, abh, 12); ctx.fill();
  ctx.fillStyle = lhot ? '#fff' : '#ffd24a'; ctx.font = 'bold 14px system-ui';
  ctx.fillText(fitText('✦ Talents' + (S.isTouch ? '' : ' (N)'), lw - 12), abx + lw / 2, actY + abh / 2);
  if ((S.talentPts || 0) > 0) {   // unspent-point badge
    ctx.fillStyle = '#ffd24a'; ctx.beginPath(); ctx.arc(abx + lw - 9, actY + 9, 9, 0, 6.2832); ctx.fill();
    ctx.fillStyle = '#1a1428'; ctx.font = 'bold 11px system-ui'; ctx.fillText(S.talentPts > 9 ? '9+' : S.talentPts + '', abx + lw - 9, actY + 10);
  }
  S.uiButtons.push({ x: abx, y: actY, w: lw, h: abh, action: () => { S.paused = false; S.showTalents = true; S.talentScroll = 0; } });
  abx += lw + aGap;
  const rhot = pointIn(aim, abx, actY, rw, abh);   // RESUME (centre, primary)
  ctx.fillStyle = rhot ? '#3aa0ff' : '#2b6fd6'; roundRect(abx, actY, rw, abh, 12); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.font = 'bold 18px system-ui';
  ctx.fillText(fitText('▶ RESUME', rw - 12), abx + rw / 2, actY + abh / 2);
  S.uiButtons.push({ x: abx, y: actY, w: rw, h: abh, action: () => { S.paused = false; } });
  abx += rw + aGap;
  const ghot = pointIn(aim, abx, actY, gw, abh);   // GIVE UP (right)
  ctx.fillStyle = ghot ? 'rgba(255,93,110,.85)' : 'rgba(255,93,110,.28)'; roundRect(abx, actY, gw, abh, 12); ctx.fill();
  ctx.fillStyle = ghot ? '#fff' : '#ff9aa6'; ctx.font = 'bold 13px system-ui';
  ctx.fillText(fitText('🏳 Give up', gw - 10), abx + gw / 2, actY + abh / 2);
  S.uiButtons.push({ x: abx, y: actY, w: gw, h: abh, action: () => {   // end the run on purpose: banks coins, logs the score & telemetry like any death
    S.paused = false;
    S.lastHitBy = 'gave up'; S.lastHitDmg = 0; S.lastHitHp = Math.round(player.hp);
    S.phoenixUsed = true;   // a chosen death is not for the Phoenix to refuse
    player.hp = 0; die();
  } });
  if (!S.isTouch) { ctx.fillStyle = '#6b76a0'; ctx.font = '11px system-ui'; ctx.textBaseline = 'alphabetic'; ctx.fillText('Esc / P to resume', px + pw / 2, actY - 6); }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}

export function drawEquipCard(it, x, y, w, h) {
  const n = S.items[it.key];
  const col = TIERS[it.tier].color;
  ctx.fillStyle = 'rgba(255,255,255,.04)'; roundRect(x, y, w, h, 10); ctx.fill();
  ctx.strokeStyle = hexA(col, 0.5); ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.font = '26px system-ui'; ctx.fillStyle = '#fff';
  ctx.fillText(it.icon, x + 12, y + h / 2);
  ctx.font = 'bold 15px system-ui'; ctx.fillStyle = col;
  ctx.fillText(fitTip(it.name + '  ×' + n, w - 60, x + 50, y + 13, 16), x + 50, y + 21);
  ctx.font = '12px system-ui'; ctx.fillStyle = '#8a97c4';
  ctx.fillText(fitTip(it.desc, w - 60, x + 50, y + 32, 16), x + 50, y + 40);
  ctx.textBaseline = 'alphabetic';
}

// ---- Upgrade shop / menu ----
export function weaponByKey(k) { return WEAPONS[WEAPON_IDX[k]] || WEAPONS[0]; }
export function fetchGlobal() {   // pull the selected version's top 10 (graceful if offline / opened as a file)
  if (typeof fetch !== 'function') { S.globalState = 'error'; return; }
  S.globalState = 'loading';
  fetch(API + '?v=' + encodeURIComponent(S.lbVersion)).then(r => r.json())
    .then(d => { S.globalScores = Array.isArray(d) ? d : []; S.globalState = 'ok'; })
    .catch(() => { S.globalState = 'error'; });
  fetch(API + '/versions').then(r => r.json())   // refresh the selector's choices alongside
    .then(d => { if (Array.isArray(d) && d.length) { if (!d.includes(GAME_VERSION)) d.unshift(GAME_VERSION); S.lbVersions = d; } })
    .catch(() => {});
}
export function submitScore(entry) {   // fire-and-forget on death; never blocks or breaks the run
  if (typeof fetch !== 'function') return;
  fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: S.save.name, wave: entry.wave, kills: entry.kills, time: entry.time, weapon: entry.weapon, v: GAME_VERSION }) })
    .then(r => r.json()).then(d => { if (Array.isArray(d) && S.lbVersion === GAME_VERSION) { S.globalScores = d; S.globalState = 'ok'; } })   // server replies with THIS build's board — only adopt it if that's the one on screen
    .catch(() => {});
}
// full run telemetry — what the player built, how far they got. Appended server-side to runs.jsonl.
export function submitRun() {
  if (typeof fetch !== 'function') return;
  const rec = {
    v: GAME_VERSION, name: S.save.name,
    wave: S.waveNum, time: S.survival, kills: S.killsRun, coins: S.coinsRun,
    coinsEarned: S.coinsEarned, coinsPeak: S.maxCoinsRun,        // economy: earned − coins(banked) = spent mid-run
    gambles: S.gambleCount, hpSacrificed: S.hpPenalty,           // gambler's-shrine pulls + blood-shrine HP paid
    bossRerolls: S.bossRerollsRun,                             // paid boss-draft rerolls (the 0.10 coin sink)
    cacheOpens: S.cacheOpens, cacheBuys: S.cacheBuys,            // item-cache engagement (0.11 draft: browsed vs bought)
    deathBy: S.lastHitBy,                                      // what landed the killing blow (oneshot forensics)
    deathDmg: Math.round(S.lastHitDmg), deathHp: Math.round(S.lastHitHp),   // the blow's size + HP it found you at — "oneshot" claims become checkable in analyze.js
    weapon: WEAPONS[S.weapon].key, evo: S.EVO ? S.EVO.name : null, evo2: S.EVO2 ? S.EVO2.name : null, build: S.buildTag, arch: S.arch,
    threat: +S.threat.toFixed(2), level: S.level, perks: S.levelPerks,   // per-run leveling: final level + drafted stat boosts
    dyn: { dmg: +S.dynDmg.toFixed(2), hp: +S.dynHp.toFixed(2) },       // dynamic-pressure dials at death — the tuning feedback loop
    items: S.items,                                    // { itemKey: count } taken this run
    dmg: Object.fromEntries(Object.entries(S.dmgBySrc).map(([k, v]) => [k, Math.round(v)])),   // { srcKey: damage } — per-source damage dealt this run
    gear: S.equipment.filter(g => g.unique).map(g => g.unique),
    upgrades: S.save.upgrades, totalRuns: S.save.runs,
  };
  fetch(API_RUN, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rec) }).catch(() => {});
}
// ---- live update detection: poll the server's build stamp; when it changes, swap in between runs ----
// At the start menu nothing is in flight (all progress persists instantly), so reload on the spot.
// Mid-run / on the death recap, just arm updateReady — startRun applies it on the next PLAY click.
S.liveStamp = null;
export function checkUpdate() {
  if (typeof fetch !== 'function') return;
  fetch('/api/version').then(r => r.json()).then(d => {
    if (!d || !d.v) return;
    if (S.liveStamp === null) { S.liveStamp = d.v; return; }   // first poll just learns the current build
    if (d.v !== S.liveStamp && !S.updateReady) {
      S.updateReady = true;
      if (!S.started) location.reload();   // idling on the start menu — swap immediately
    }
  }).catch(() => {});
}
export function lbRank(i, x, y, w, mine) {   // shared rank cell + highlight row for "yours"
  if (mine) { ctx.fillStyle = 'rgba(125,255,176,.12)'; roundRect(x - 8, y - 14, w, 26, 6); ctx.fill(); }
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillStyle = i === 0 ? '#ffd66e' : '#7e8bb5'; ctx.font = 'bold 14px system-ui';
  ctx.fillText((i + 1) + '.', x, y);
}
export function leaderboardMenu() {
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.9)'; ctx.fillRect(0, 0, S.W, S.H);
  const pw = Math.min(580, S.W - 32);
  const verOwnRow = pw < 470 && S.lbTab === 'global';   // narrow: version picker drops to its own row under the tabs (else it overlaps LOCAL)
  const vOff = verOwnRow ? 36 : 0;
  // size the panel to the row count so the BACK button sits just under the list, not after a big gap
  const _rows = S.lbTab === 'global'
    ? (S.globalState === 'ok' ? Math.max(1, (S.globalScores || []).length) : 1)
    : Math.max(1, (S.save.scores || []).length);
  const ph = Math.min(S.H - 32, 138 + vOff + _rows * 30 + 16 + 80);
  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(12,18,38,.95)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = 'bold 28px system-ui';
  ctx.fillText('🏆 LEADERBOARD', px + 28, py + 44);

  // Global / Local tabs
  let tx = px + 28;
  for (const [tkey, label] of [['global', 'GLOBAL'], ['local', 'LOCAL']]) {
    const active = S.lbTab === tkey, tw = 96, ty = py + 58, th = 30;
    ctx.fillStyle = active ? '#2b6fd6' : 'rgba(255,255,255,.06)';
    roundRect(tx, ty, tw, th, 8); ctx.fill();
    ctx.fillStyle = active ? '#fff' : '#9fb0e0'; ctx.font = 'bold 13px system-ui';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, tx + tw / 2, ty + th / 2 + 1);
    S.uiButtons.push({ x: tx, y: ty, w: tw, h: th, action: () => { S.lbTab = tkey; if (tkey === 'global' && S.globalState !== 'ok') fetchGlobal(); } });
    tx += tw + 8;
  }
  // version selector (global board only): ◂ v0.17 ▸ — each balance patch is its own board, newest first
  if (S.lbTab === 'global') {
    const vy = py + 58 + vOff, vh = 30, aw = 26, lw = 92;
    const vbx = verOwnRow ? px + 28 : px + pw - 28 - (aw + lw + aw + 8);
    const vi = Math.max(0, S.lbVersions.indexOf(S.lbVersion));
    const setV = nv => { if (nv && nv !== S.lbVersion) { S.lbVersion = nv; S.globalScores = null; fetchGlobal(); } };
    const arrows = [   // lbVersions is newest-first: ◂ steps to older, ▸ back toward current
      { lbl: '◂', x: vbx,                ok: vi < S.lbVersions.length - 1, go: () => setV(S.lbVersions[vi + 1]) },
      { lbl: '▸', x: vbx + aw + lw + 8,  ok: vi > 0,                     go: () => setV(S.lbVersions[vi - 1]) },
    ];
    for (const a of arrows) {
      ctx.fillStyle = a.ok ? 'rgba(255,255,255,.10)' : 'rgba(255,255,255,.03)';
      roundRect(a.x, vy, aw, vh, 8); ctx.fill();
      ctx.fillStyle = a.ok ? '#9fb0e0' : '#4a5478'; ctx.font = 'bold 13px system-ui';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(a.lbl, a.x + aw / 2, vy + vh / 2 + 1);
      if (a.ok) S.uiButtons.push({ x: a.x, y: vy, w: aw, h: vh, action: a.go });
    }
    ctx.fillStyle = 'rgba(255,255,255,.06)'; roundRect(vbx + aw + 4, vy, lw, vh, 8); ctx.fill();
    ctx.fillStyle = S.lbVersion === GAME_VERSION ? '#7fb6ff' : '#9fb0e0'; ctx.font = 'bold 13px system-ui';
    ctx.fillText('v' + S.lbVersion + (S.lbVersion === GAME_VERSION ? '' : ' ·old'), vbx + aw + 4 + lw / 2, vy + vh / 2 + 1);
  }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

  const lx = px + 28, headY = py + 116 + vOff, rowY = py + 138 + vOff;
  const cN = px + 56, cW = px + pw * 0.46, cK = px + pw * 0.62, cT = px + pw * 0.78, cG = px + pw - 46;

  if (S.lbTab === 'global') {
    if (S.globalState === 'idle') fetchGlobal();   // lazy first load
    ctx.fillStyle = '#6b76a0'; ctx.font = 'bold 11px system-ui';
    ctx.fillText('#  NAME', lx, headY); ctx.fillText('WAVE', cW, headY); ctx.fillText('KILLS', cK, headY); ctx.fillText('GUN', cG, headY);
    if (S.globalState === 'loading') { ctx.fillStyle = '#9fb0e0'; ctx.font = '14px system-ui'; ctx.fillText('Connecting…', lx, rowY + 14); }
    else if (S.globalState === 'error') { ctx.fillStyle = '#ff9d6e'; ctx.font = '14px system-ui'; ctx.fillText('Global board offline — Local still works.', lx, rowY + 14); }
    else {
      const g = S.globalScores || [];
      if (!g.length) { ctx.fillStyle = '#6b76a0'; ctx.font = '14px system-ui'; ctx.fillText('No scores yet — be the first!', lx, rowY + 14); }
      else {
        let ry = rowY;
        g.forEach((s, i) => {
          lbRank(i, lx, ry, pw - 40, s.name === S.save.name);
          ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 14px system-ui'; ctx.fillText((s.name || 'Player').slice(0, 12), cN, ry);
          ctx.fillStyle = '#cdd6ff'; ctx.font = '13px system-ui';
          ctx.fillText('' + s.wave, cW, ry); ctx.fillText('' + s.kills, cK, ry);
          ctx.font = '16px system-ui'; ctx.fillText(weaponByKey(s.weapon).icon, cG, ry);
          ry += 30;
        });
        ctx.textBaseline = 'alphabetic';
      }
    }
  } else {
    ctx.fillStyle = '#6b76a0'; ctx.font = 'bold 11px system-ui';
    ctx.fillText('#  WAVE', lx, headY); ctx.fillText('KILLS', cK, headY); ctx.fillText('TIME', cT, headY); ctx.fillText('GUN', cG, headY);
    const scores = S.save.scores || [];
    if (!scores.length) { ctx.fillStyle = '#6b76a0'; ctx.font = '14px system-ui'; ctx.fillText('No runs yet — go set a record.', lx, rowY + 14); }
    else {
      let ry = rowY;
      scores.forEach((s, i) => {
        lbRank(i, lx, ry, pw - 40, s.t === S.lastScoreT);
        ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 16px system-ui'; ctx.fillText('Wave ' + s.wave, lx + 26, ry);
        ctx.fillStyle = '#cdd6ff'; ctx.font = '13px system-ui';
        ctx.fillText('' + s.kills, cK, ry); ctx.fillText(fmtTime(s.time), cT, ry);
        ctx.font = '16px system-ui'; ctx.fillText(weaponByKey(s.weapon).icon, cG, ry);
        ry += 30;
      });
      ctx.textBaseline = 'alphabetic';
    }
  }

  // back button
  const bw = 200, bh = 50, bx = px + (pw - bw) / 2, by = py + ph - bh - 20;
  const hot = pointIn(aim, bx, by, bw, bh);
  ctx.fillStyle = hot ? '#3aa0ff' : '#2b6fd6'; roundRect(bx, by, bw, bh, 12); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.font = 'bold 18px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('◂ BACK', bx + bw / 2, by + bh / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => { S.showLeaderboard = false; } });
}
